import { FC, useEffect, useState } from 'react';
import { useAppContext } from '@/context/AppContext';
import World from '../world/World';
import './Creator.css';
import Stack from '@/shared/components/Stack/Stack';
import Sidebar from '@/shared/components/Sidebar/Sidebar';
import Topbar from '@/shared/components/Topbar/Topbar';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import Button from '@/shared/components/Button/Button';
import Tabs from '@/shared/components/Tabs/Tabs';
import Tab from '@/shared/components/Tab/Tab';
import Text from '@/shared/components/Text/Text';
import DataView from '@/shared/components/DataView/DataView';
import DataForm from '@/shared/components/DataForm/DataForm';
import AddItemPanel from './components/AddItemPanel/AddItemPanel';
import Clock from '../clock/Clock';
import LinkEditor from './components/LinkEditor/LinkEditor';
import { Link, Action } from '@/types/core/Link';
import Logo from '@/shared/components/Logo/Logo';

// Transform mode type
type TransformMode = 'translate' | 'rotate' | 'scale';

// Selected item type - can be a regular item or a link action
interface SelectedLinkAction {
  type: 'linkAction';
  id: string;
  linkId: string;
  actionIndex: number;
  action: Action;
}

type SelectedItemType = string | SelectedLinkAction | null;

const Creator: FC = () => {
  const { createWorld, state, setActiveWorld, activeWorld, selectedItem, selectItem, updateItem } = useAppContext();
  const [transformMode, setTransformMode] = useState<TransformMode>('translate');
  const [selectedLinkAction, setSelectedLinkAction] = useState<SelectedLinkAction | null>(null);
  const [activeTabId, setActiveTabId] = useState<string>('world');
  const { worldId } = useParams();
  const navigate = useNavigate();

  // Set active world or create a new one if none exists
  useEffect(() => {
    if (worldId) {
      if (worldId in state.worlds) {
        setActiveWorld(worldId);
      } else {
        navigate('/');
      }
    } else if (Object.keys(state.worlds).length === 0) {
      const name = 'Default World';
      createWorld(name, 'default');
    }
  }, [worldId, state.worlds]);

  // Check if the selected link action still exists
  useEffect(() => {
    if (selectedLinkAction && selectedItem && selectedItem.type === 'link') {
      const link = selectedItem as Link;
      // If the action no longer exists, clear the selectedLinkAction state
      if (selectedLinkAction.actionIndex >= link.actions.length) {
        setSelectedLinkAction(null);
      }
    }
  }, [selectedItem, selectedLinkAction]);

  // Handle selecting an item or link action
  const handleSelectItem = (item: any) => {
    if (item.type === 'linkAction') {
      // It's a link action
      setSelectedLinkAction(item);
      selectItem(item.linkId);
    } else {
      // It's a regular item
      setSelectedLinkAction(null);
      selectItem(item.id);
    }
  };

  // Handle going back from item editor
  const handleBackFromEditor = () => {
    setSelectedLinkAction(null);
    selectItem(null);
  };

  const renderTransformControls = () => {
    if (!selectedItem || (selectedItem.type !== 'mesh' && selectedItem.type !== 'light')) {
      return null;
    }

    return (
      <Stack direction="horizontal" gap="sm" className="transform-controls">
        <Button
          icon="Move"
          label="Move"
          variant={transformMode === 'translate' ? 'primary' : 'secondary'}
          onClick={() => setTransformMode('translate')}
        />
        <Button
          icon="RotateCw"
          label="Rotate"
          variant={transformMode === 'rotate' ? 'primary' : 'secondary'}
          onClick={() => setTransformMode('rotate')}
        />
        <Button
          icon="Maximize"
          label="Scale"
          variant={transformMode === 'scale' ? 'primary' : 'secondary'}
          onClick={() => setTransformMode('scale')}
        />
      </Stack>
    );
  };

  const renderItemEditor = () => {
    if (!selectedItem) return null;
    if (!activeWorld) return null;

    // Use LinkEditor for links
    if (selectedItem.type === 'link') {
      return (
        <LinkEditor
          link={selectedItem as Link}
          world={activeWorld}
          onBack={handleBackFromEditor}
          onChange={(changes) => updateItem(selectedItem.id, changes)}
          selectItem={selectItem}
          selectedActionIndex={selectedLinkAction ? selectedLinkAction.actionIndex : null}
          setSelectedActionIndex={(index) => {
            if (index === null) {
              setSelectedLinkAction(null);
            } else {
              // Create a new selectedLinkAction object
              setSelectedLinkAction({
                type: 'linkAction',
                id: `${selectedItem.id}-action-${index}`,
                linkId: selectedItem.id,
                actionIndex: index,
                action: (selectedItem as Link).actions[index]
              });
            }
          }}
        />
      );
    }

    // Default editor for other item types
    return (
      <Stack direction="vertical" gap="md" className="item-editor">
        <Stack direction="horizontal" gap="md" align="center" className="item-editor__header">
          <Button onClick={handleBackFromEditor} icon="ArrowLeft" label="Back" />
          <Text variant="h2">{selectedItem.name}</Text>
        </Stack>
        {renderTransformControls()}
        <Stack direction="vertical" gap="md" className="item-editor__content">
          <DataForm
            fields={selectedItem.fields}
            values={selectedItem}
            onChange={(changes) => updateItem(selectedItem.id, changes)}
            world={activeWorld}
            selectItem={selectItem}
          />
        </Stack>
      </Stack>
    );
  };

  const renderTabs = () => {
    if (!activeWorld) return null;

    // Prepare links with their actions as children
    const linksWithActions = Object.values(activeWorld.items)
      .filter(item => item.type === 'link')
      .map((link: any) => {
        const children = link.actions.map((action: Action, index: number) => ({
          id: `${link.id}-action-${index}`,
          name: action.name,
          actionType: action.type === 'minmax' ? 'Min/Max' : 'Toggle',
          icon: 'Sliders',
          type: 'linkAction',
          linkId: link.id,
          actionIndex: index,
          action: action
        }));

        return {
          ...link,
          children
        };
      });

    return (
      <Tabs activeTab={activeTabId} onTabChange={setActiveTabId}>
        <Tab title="World" id="world">
          <Stack direction="vertical" gap="md">
            <DataView
              data={[
                ...Object.values(activeWorld.items).filter(item => item.type === 'camera'),
                ...Object.values(activeWorld.items).filter(item => item.type === 'mesh'),
                ...Object.values(activeWorld.items).filter(item => item.type === 'light'),
              ]}
              onSelect={handleSelectItem}
              selectedId={selectedItem?.id}
            />
          </Stack>
        </Tab>
        <Tab title="Materials" id="materials">
          <Stack direction="vertical" gap="md">
            <DataView
              data={Object.values(activeWorld.items).filter(item => item.type === 'material')}
              onSelect={handleSelectItem}
              selectedId={selectedItem?.id}
            />
          </Stack>
        </Tab>
        <Tab title="Effects" id="effects">
          <Stack direction="vertical" gap="md">
            <DataView
              data={Object.values(activeWorld.items).filter(item => item.type === 'effect')}
              onSelect={handleSelectItem}
              selectedId={selectedItem?.id}
            />
          </Stack>
        </Tab>
        <Tab title="Links" id="links">
          <Stack direction="vertical" gap="md">
            <DataView
              data={linksWithActions}
              onSelect={handleSelectItem}
              selectedId={selectedLinkAction ? selectedLinkAction.id : selectedItem?.id}
              renderChildren={true}
            />
          </Stack>
        </Tab>
      </Tabs>
    );
  };

  return (
    <Stack className="creator" gap="no">
      <Topbar>
        <RouterLink to="/" className="topbar__back">
          <Logo />
        </RouterLink>
        <Clock />
      </Topbar>
      <Sidebar>
        {selectedItem ? renderItemEditor() : renderTabs()}
      </Sidebar>
      <Stack className="main" direction="vertical" fill>
        <World transformMode={transformMode} />
        <AddItemPanel />
      </Stack>
    </Stack>
  );
};

export default Creator; 